import type {
  ChartViewId,
  CustomTimeSegment,
  SpectralWorkspaceSession,
} from '../types';
import {
  computeDescriptiveStatistics,
  type DescriptiveStatRow,
} from '../utils/computeDescriptiveStatistics';
import {
  getWorkspaceAvailableSeconds,
  resolveSegmentBounds,
} from '../utils/resolveSpectraSegment';
import {
  resolveTrendExportViewIds,
  resolveViewSheetLabel,
} from './computeTrendExportSeries';

export type StatisticsValidationExportContext = {
  readySessions: SpectralWorkspaceSession[];
  customSegments: CustomTimeSegment[];
  trendStart: string | null;
  trendEnd: string | null;
};

export const buildStatisticsValidationSheetName = (viewSheetLabel: string) =>
  `Validazione statistica - ${viewSheetLabel}`;

const formatExportNumber = (value: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : '';

const formatOutlierLabel = (
  session: DescriptiveStatRow['outlierSessions'][number]
) => {
  const patientSuffix = session.patientCode ? ` (${session.patientCode})` : '';
  return `Sessione ${session.sessionId}${patientSuffix}: valore ${session.value.toFixed(2)}, distanza ${session.distance.toFixed(2)}`;
};

export const buildStatisticsValidationDataRows = (
  rows: DescriptiveStatRow[],
  viewSheetLabel: string
) =>
  rows.map(row => ({
    Tratto: viewSheetLabel,
    Segnale: row.feature,
    Sessioni: row.sessionCount,
    'Media gruppo': formatExportNumber(row.mean),
    Variabilita: formatExportNumber(row.std),
    Min: formatExportNumber(row.min),
    Max: formatExportNumber(row.max),
    'Coeff variabilita': formatExportNumber(row.coefficientOfVariation),
    'Outlier 1': row.outlierSessions[0]
      ? formatOutlierLabel(row.outlierSessions[0])
      : '',
    'Outlier 2': row.outlierSessions[1]
      ? formatOutlierLabel(row.outlierSessions[1])
      : '',
    'Outlier 3': row.outlierSessions[2]
      ? formatOutlierLabel(row.outlierSessions[2])
      : '',
  }));

export const computeStatisticsValidationRowsForView = (
  context: StatisticsValidationExportContext,
  viewId: ChartViewId
) => {
  const availableSeconds = getWorkspaceAvailableSeconds(context.readySessions);
  const segmentBounds = resolveSegmentBounds({
    activeViewId: viewId,
    customSegments: context.customSegments,
    availableSeconds,
    trendStart: context.trendStart,
    trendEnd: context.trendEnd,
  });

  if (!segmentBounds) {
    return [];
  }

  return computeDescriptiveStatistics(
    context.readySessions,
    segmentBounds.startSecond,
    segmentBounds.endSecond
  );
};

export const buildStatisticsValidationExportForView = (
  context: StatisticsValidationExportContext,
  viewId: ChartViewId
) => {
  const viewSheetLabel = resolveViewSheetLabel(viewId, context.customSegments);
  const rows = computeStatisticsValidationRowsForView(context, viewId);

  return buildStatisticsValidationDataRows(rows, viewSheetLabel);
};

export const buildStatisticsValidationExportSheets = (
  context: StatisticsValidationExportContext
) =>
  resolveTrendExportViewIds(context.customSegments).map(viewId => ({
    name: buildStatisticsValidationSheetName(
      resolveViewSheetLabel(viewId, context.customSegments)
    ),
    rows: buildStatisticsValidationExportForView(context, viewId),
  }));
